/* =====================================================
   BB Filter Sidebar — toggle, search, chip bar, price slider

   Live since 2026-05-07.
   v9: Added dual-handle price slider with bidirectional
   sync to the min/max number inputs.
   2026-06-17 UX fix: price slider now commits on release
   (the `change` event) instead of firing a product reload
   on every step of the drag.
   2026-06-17 (pm) fix: active chip bar now re-renders from
   the params of the render that just ran (searchParamsPrev)
   instead of window.location.search. The old code raced the
   URL update and, when a section was served from cache, fell
   back to the last fetched (still-filtered) entry — which is
   why the price pill stayed stuck after Clear All.

   - Click a section header to expand/collapse it.
   - Type in a section's search input to filter the
     visible options inside that section.
   - Drag price slider thumbs OR type in the price
     number inputs; both stay in sync. The price filter
     commits once on release; checkboxes fire via
     facets.js's debounced input listener.
   - On every `filters:rerendered` event:
       1. Re-apply user's expanded sections
       2. Replace the chip bar with the new server-
          rendered version
       3. Re-init the price slider against the new DOM

   AJAX form submission and product grid update are
   handled entirely by Adelle's facets.js via the
   <facet-filters-form> custom element.
   ===================================================== */

(function () {
  'use strict';

  var expandedFilters = new Set();

  function onClick(e) {
    var header = e.target.closest('.bb-filter-header');
    if (!header) return;

    var group = header.closest('.bb-filter-group');
    var filterId = group ? group.id : null;
    var expanded = header.getAttribute('aria-expanded') === 'true';

    if (expanded) {
      header.setAttribute('aria-expanded', 'false');
      if (filterId) expandedFilters.delete(filterId);
    } else {
      header.setAttribute('aria-expanded', 'true');
      if (filterId) expandedFilters.add(filterId);
    }
  }

  function restoreExpandedState() {
    expandedFilters.forEach(function (filterId) {
      var group = document.getElementById(filterId);
      if (!group) return;
      var header = group.querySelector('.bb-filter-header');
      if (header) header.setAttribute('aria-expanded', 'true');
    });
  }

  function onSearchInput(e) {
    if (!e.target.classList.contains('bb-filter-search')) return;
    e.stopPropagation();

    var query = e.target.value.trim().toLowerCase();
    var panel = e.target.closest('.bb-filter-panel');
    if (!panel) return;

    var items = panel.querySelectorAll('.bb-filter-item');
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var text = item.getAttribute('data-search-text') || '';
      if (query === '' || text.indexOf(query) !== -1) {
        item.style.display = '';
      } else {
        item.style.display = 'none';
      }
    }
  }

  function onKeydown(e) {
    if (e.key !== 'Enter') return;
    if (!e.target.classList.contains('bb-filter-search')) return;
    e.preventDefault();
  }

  function onSubmit(e) {
    if (e.target && e.target.id === 'FacetFiltersForm') {
      e.preventDefault();
    }
  }

  // ----------------------------------------------------
  // Active filter chip bar (pulled from facets.js cache)
  // ----------------------------------------------------
  function updateActiveBar() {
    try {
      if (typeof FacetFiltersForm === 'undefined') return;
      var currentForm = document.querySelector('#FacetFiltersForm');
      if (!currentForm) return;

      // Source of truth for the render that just happened. facets.js sets
      // searchParamsPrev synchronously at the start of renderPage, so it is
      // reliable even when the section is served from cache (window.location
      // .search can still be stale at this moment — that race left the price
      // pill stuck after Clear All).
      var target = (FacetFiltersForm.searchParamsPrev != null)
        ? FacetFiltersForm.searchParamsPrev
        : window.location.search.slice(1);

      var hasActiveFilters = target.indexOf('filter.') !== -1;

      // Find the cached section HTML whose params match this exact render.
      var entry = null;
      var data = FacetFiltersForm.filterData || [];
      for (var i = data.length - 1; i >= 0; i--) {
        var e = data[i];
        if (!e || !e.url) continue;
        var params;
        try {
          var u = new URL(e.url, window.location.origin);
          u.searchParams.delete('section_id');
          params = u.searchParams.toString();
        } catch (_) { continue; }
        if (params === target) { entry = e; break; }
      }

      var currentBar = currentForm.querySelector('.bb-filter-active-bar');

      if (!entry || !entry.html) {
        // No matching cache entry. If this render carries no active filters
        // (e.g. Clear All), the bar must be gone — never leave a stale pill.
        if (!hasActiveFilters && currentBar) currentBar.remove();
        return;
      }

      var doc = new DOMParser().parseFromString(entry.html, 'text/html');
      var newForm = doc.querySelector('#FacetFiltersForm');
      if (!newForm) return;
      var newBar = newForm.querySelector('.bb-filter-active-bar');

      if (newBar && currentBar) {
        currentBar.outerHTML = newBar.outerHTML;
      } else if (newBar && !currentBar) {
        currentForm.insertBefore(newBar.cloneNode(true), currentForm.firstChild);
      } else if (!newBar && currentBar) {
        currentBar.remove();
      }
    } catch (err) {
      console.warn('bb-filter-sidebar: chip bar update failed', err);
    }
  }

  // ----------------------------------------------------
  // Price slider — dual-handle range with sync to numbers
  // ----------------------------------------------------
  function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
  }

  function initPriceSliders() {
    document.querySelectorAll('.bb-price-slider').forEach(function (slider) {
      if (slider.dataset.bbInit === '1') return;
      slider.dataset.bbInit = '1';

      var minInput = slider.querySelector('.bb-price-slider-input--min');
      var maxInput = slider.querySelector('.bb-price-slider-input--max');
      var fill = slider.querySelector('.bb-price-slider-fill');
      if (!minInput || !maxInput || !fill) return;

      var minNumber = document.getElementById(slider.dataset.minTarget);
      var maxNumber = document.getElementById(slider.dataset.maxTarget);

      var labelsWrap = slider.parentElement.querySelector('.bb-price-slider-labels');
      var minLabel = labelsWrap ? labelsWrap.querySelector('.bb-price-slider-label-min') : null;
      var maxLabel = labelsWrap ? labelsWrap.querySelector('.bb-price-slider-label-max') : null;

      var maxRange = parseInt(slider.dataset.max, 10) || 100;
      var currencySymbol = (minLabel && minLabel.textContent.charAt(0)) || '$';
      slider._lastMoved = 'max';

      function readSliderValues() {
        var minV = parseInt(minInput.value, 10);
        var maxV = parseInt(maxInput.value, 10);
        if (isNaN(minV)) minV = 0;
        if (isNaN(maxV)) maxV = maxRange;
        return [minV, maxV];
      }

      function applyVisualState() {
        var vals = readSliderValues();
        var minV = vals[0], maxV = vals[1];
        var leftPct = (minV / maxRange) * 100;
        var rightPct = 100 - (maxV / maxRange) * 100;
        fill.style.left = leftPct + '%';
        fill.style.right = rightPct + '%';
        if (minLabel) minLabel.textContent = currencySymbol + minV;
        if (maxLabel) maxLabel.textContent = currencySymbol + maxV;
      }

      function preventCrossing() {
        var vals = readSliderValues();
        var minV = vals[0], maxV = vals[1];
        if (minV >= maxV) {
          if (slider._lastMoved === 'min') {
            minInput.value = clamp(maxV - 1, 0, maxRange);
          } else {
            maxInput.value = clamp(minV + 1, 0, maxRange);
          }
        }
      }

      function syncToNumberInputs() {
        var vals = readSliderValues();
        if (minNumber) minNumber.value = vals[0];
        if (maxNumber) maxNumber.value = vals[1];
      }

      function onSliderInput(which) {
        return function () {
          slider._lastMoved = which;
          preventCrossing();
          applyVisualState();
          syncToNumberInputs();
        };
      }

      minInput.addEventListener('input', onSliderInput('min'));
      maxInput.addEventListener('input', onSliderInput('max'));

      // Commit the price filter on release only, not on every drag step.
      // 1) Stop the slider's own `input` from bubbling to the form, so
      //    facets.js does not fire a product reload mid-drag.
      // 2) On `change` (pointer release / keyboard commit), nudge a price
      //    number field (which facets.js listens to) so exactly one reload
      //    runs with the final min/max values.
      function stopInputBubble(ev) { ev.stopPropagation(); }
      minInput.addEventListener('input', stopInputBubble);
      maxInput.addEventListener('input', stopInputBubble);

      function commitPrice() {
        var field = maxNumber || minNumber;
        if (field) field.dispatchEvent(new Event('input', { bubbles: true }));
      }
      minInput.addEventListener('change', commitPrice);
      maxInput.addEventListener('change', commitPrice);

      function onNumberInput(which) {
        return function () {
          var n = parseFloat((which === 'min' ? minNumber : maxNumber).value);
          if (isNaN(n)) return;
          var rounded = clamp(Math.round(n), 0, maxRange);
          if (which === 'min') {
            minInput.value = rounded;
            slider._lastMoved = 'min';
          } else {
            maxInput.value = rounded;
            slider._lastMoved = 'max';
          }
          applyVisualState();
        };
      }
      if (minNumber) minNumber.addEventListener('input', onNumberInput('min'));
      if (maxNumber) maxNumber.addEventListener('input', onNumberInput('max'));

      applyVisualState();
    });
  }

  function onRerender() {
    restoreExpandedState();
    updateActiveBar();
    initPriceSliders();
  }

  document.addEventListener('click', onClick);
  document.addEventListener('input', onSearchInput, true);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('submit', onSubmit);
  document.addEventListener('filters:rerendered', onRerender);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPriceSliders);
  } else {
    initPriceSliders();
  }
})();
